import { FinancialCalculationService } from './FinancialCalculationService.js';
import {
  calculateMonthlyCustomerMetrics,
  toStandardizedCustomerData,
} from './customer-projection.js';
import type { MonthlyCustomerMetrics, StandardizedCustomerData } from './types.js';

/** Accept legacy shape (customerCount) or current MonthlyCustomerMetrics (totalCustomers) */
export function normalizeCustomerDataInput(
  customerData?: Partial<StandardizedCustomerData> & {
    customerCount?: number;
    newCustomersThisMonth?: number;
    totalMonthlyRevenue?: number;
  }
): StandardizedCustomerData | undefined {
  if (!customerData) return undefined;

  const totalCustomers =
    customerData.totalCustomers ?? customerData.customerCount ?? 0;

  return {
    totalCustomers,
    newCustomers: customerData.newCustomers ?? customerData.newCustomersThisMonth ?? 0,
    customersAcquired: customerData.customersAcquired,
    churnedCustomers: customerData.churnedCustomers ?? 0,
    subscriptionRevenue:
      customerData.subscriptionRevenue ?? customerData.totalMonthlyRevenue ?? 0,
    acquisitionCosts: customerData.acquisitionCosts ?? 0,
    averageRevenuePerCustomer: customerData.averageRevenuePerCustomer ?? 0,
    churnRateMonthly: customerData.churnRateMonthly,
  };
}

export function calculateExpenseAtMonth(
  expense: Record<string, unknown>,
  targetMonth: Date,
  totalMonthlyRevenue: number = 0,
  customerData?: Parameters<typeof normalizeCustomerDataInput>[0]
): number {
  const standardized = FinancialCalculationService.standardizeExpenseFromAny(expense);
  const customers = normalizeCustomerDataInput(customerData);
  return FinancialCalculationService.calculateExpenseAtMonth(
    standardized,
    targetMonth,
    totalMonthlyRevenue,
    customers
  ).calculatedAmount;
}

export function calculateRevenueAtMonth(
  revenue: Record<string, unknown>,
  targetMonth: Date,
  customerData?: Parameters<typeof normalizeCustomerDataInput>[0]
): number {
  const standardized = FinancialCalculationService.standardizeRevenueFromAny(revenue);
  const customers = normalizeCustomerDataInput(customerData);
  return FinancialCalculationService.calculateRevenueAtMonth(
    standardized,
    targetMonth,
    customers
  ).calculatedAmount;
}

export { calculateMonthlyCustomerMetrics, toStandardizedCustomerData };
export type { MonthlyCustomerMetrics };
